import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { CACHE_EXPIRATION_TIME, DESKTOP_SCREEN_SIZE, PersonalConstants } from 'app/common/personalConstants';
import { ContractorState } from 'app/shared/models/claim-tracker.model';
import { ClaimsFnolService } from 'app/shared/services/claims-fnol.service';
import { ClaimsTrackerService } from 'app/shared/services/claims-tracker.service';
import { LoggerService } from 'app/shared/services/logger.service';
import { SessionService } from 'app/shared/services/session.service';
import { UtilService } from 'app/shared/services/util.service';

@Component({
  selector: 'is-timeline',
  templateUrl: './timeline.component.html',
  styleUrl: './timeline.component.scss',
  standalone: false
})
export class TimelineComponent implements OnInit {
    timeLineSteps = {
        'CLAIM_OPENED': { label: 'Claim opened', mobileLabel: 'Opened', status: 'NOT_STARTED' },
        'INSPECTION': { label: 'Inspection', mobileLabel: 'Inspection', status: 'NOT_STARTED' },
        'ESTIMATE': { label: 'Estimate', mobileLabel: 'Estimate', status: 'NOT_STARTED' },
        'PAYMENTS': { label: 'Payments', mobileLabel: 'Payments', status: 'NOT_STARTED' },
        'CLAIM_CLOSED': { label: 'Claim closed', mobileLabel: 'Closed', status: 'NOT_STARTED' }
    };

    stepDescription: string;
    isLoading = false;

    filteredSteps: any[] = [];
    @Input() claimNumber!: string;
    @Input() contactPublicId!: string;
    @Output() goToTab = new EventEmitter<string>();
    @Output() hideContractor = new EventEmitter<boolean>();
    @Output() setPaymentsNotApplicable = new EventEmitter<boolean>();
    @Output() loadTimeEmitter = new EventEmitter<number>();
    @Output() showTimeline = new EventEmitter<boolean>();
    @Input() useCaching!: boolean;
    hasNotes = false;
    hasOffering = false;
    hasClosedOutcome = false;
    hasTimeline = false;
    closedOutcome: any;

    constructor(
        private router: Router,
        private claimsFnolService: ClaimsFnolService,
        private claimsTrackerService: ClaimsTrackerService,
        private sessionService: SessionService,
        private logger: LoggerService,
        private utils: UtilService
    ) {}

    delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    ngOnInit(): void {
        this.logger.getStartLoadTimer();
        this.isLoading = true;
        if (this.claimNumber) {
            const CLAIM_NOTES_CACHE_DATA = this.useCaching ? this.utils.getWithExpiry('CLAIM_NOTES_DATA') : undefined;
            if (
                typeof CLAIM_NOTES_CACHE_DATA === 'undefined' ||
                CLAIM_NOTES_CACHE_DATA.id !== this.claimNumber
            ) {
                this.claimsFnolService.buildgetNoteRequest(this.claimNumber).subscribe({
                    next: (response) => {
                        if (response && response.notes && response.notes?.length > 0) {
                            this.hasNotes = true;
                        }
                        this.utils.setWithExpiry(
                            'CLAIM_NOTES_DATA',
                            response,
                            CACHE_EXPIRATION_TIME,
                            this.claimNumber
                        );
                    }
                });
            } else {
                const response = CLAIM_NOTES_CACHE_DATA.value;
                this.hasNotes = response && response.notes && response.notes?.length > 0 || false;
            }

            const TIMELINE_CACHE_DATA = this.useCaching ? this.utils.getWithExpiry('TIMELINE_DATA') : undefined;
            if (
                typeof TIMELINE_CACHE_DATA === 'undefined' ||
                TIMELINE_CACHE_DATA.id !== this.claimNumber
            ) {
                this.getTimelineDetails();
            } else {
                const response = TIMELINE_CACHE_DATA.value;
                this.loadTimeLineData(response);
            }
        } else {
            this.logger.info('No Claim number found');
            this.showTimeline.emit(true);
            this.router.navigate([PersonalConstants.URLS.error500]);
        }
    }

    goToSupportTab(): void {
        if (window.innerWidth <= DESKTOP_SCREEN_SIZE) {
            this.router.navigate([`/property/tracker/support`]);
        } else {
            this.goToTab.emit('Support');
        }
    }

    getLabelCss(i: any): string {
        if (this.filteredSteps[i].status === 'COMPLETE' || this.filteredSteps[i].status === 'NOT_APPLICABLE') {
            return 'completed-label';
        } else if (this.filteredSteps[i].status === 'IN_PROGRESS' || ( i !== 0 && this.filteredSteps[i - 1].status === 'COMPLETE')) {
            return 'in-progress-label';
        } else {
            return 'not-started-label';
        }
    }

    async getTimelineDetails(): Promise<void> {
        await this.delay(5000);
        if (this.contactPublicId === 'no-contact-id') {
            this.filteredSteps = Object.values(this.timeLineSteps).filter(
                (step) => step.status !== 'SKIPPED'
            );
            this.hasTimeline = true;
            this.isLoading = false;
            return;
        }
        this.claimsTrackerService.getTimelineDetails(this.claimNumber, this.contactPublicId)
        .subscribe({
            next: (response) => {
                if (!response || !response.timelineCategories) {
                    this.logger.error('Failed to get timeline categories');
                    this.isLoading = false;
                    return;
                }
                this.utils.setWithExpiry(
                    'TIMELINE_DATA',
                    response,
                    CACHE_EXPIRATION_TIME,
                    this.claimNumber
                );
                this.loadTimeLineData(response);
            },
            error: (error) => {
                if (error.status && error.status === 404 ) {
                    this.filteredSteps = Object.values(this.timeLineSteps).filter(
                        (step) => step.status !== 'SKIPPED'
                    );
                    this.hasTimeline = true;
                }
                this.isLoading = false;
                this.logPageLoadTime();
                this.showTimeline.emit(true);
            }
        });
    }

    loadTimeLineData(response: any): void {
        response.timelineCategories.forEach((category) => {
            this.timeLineSteps[category.categoryName].status = category.status;
        });
        this.filteredSteps = Object.values(this.timeLineSteps).filter(
            (step) => step.status !== 'SKIPPED'
        );
        if (this.timeLineSteps['INSPECTION'].status === 'SKIPPED') {
            this.hideContractor.emit(true);
        }
        this.showTimeline.emit(true);
        if (this.timeLineSteps['PAYMENTS'].status === 'NOT_APPLICABLE') {
            this.setPaymentsNotApplicable.emit(true);
        }
        this.hasOffering = this.sessionService.getSessionItem('contractor') !== ContractorState.NO_OFFERINGS;
        this.hasClosedOutcome = this.sessionService.getSessionItem('hasClosedOutcome') === 'true';
        this.closedOutcome = this.sessionService.getSessionItem('closedOutcome');
        this.loadDescription();
        this.hasTimeline = true;
        this.isLoading = false;
        this.logPageLoadTime();
    }

    private logPageLoadTime(): void {
        const loadTime = this.logger.getStopLoadTimer();
        this.logger.info(`Timeline Component Load Time`, loadTime);
        // Emit load time to parent component
        this.loadTimeEmitter.emit(loadTime);
    }

    loadDescription(): void {
        if (this.timeLineSteps['CLAIM_OPENED'].status === 'COMPLETE' && this.timeLineSteps['INSPECTION'].status === 'IN_PROGRESS' && !this.hasNotes && !this.hasOffering) {
            this.stepDescription = 'Time to select a contractor & schedule a damage inspection.';
        } else if (this.timeLineSteps['CLAIM_OPENED'].status === 'COMPLETE' && this.timeLineSteps['INSPECTION'].status === 'IN_PROGRESS' && (this.hasNotes || this.hasOffering)) {
            this.stepDescription = 'Your contractor will contact you to get your inspection scheduled.';
        } else if (this.timeLineSteps['ESTIMATE'].status === 'IN_PROGRESS') {
            this.stepDescription = 'We\'re working on your estimate. We\'ll notify you when it\'s ready to review.';
        } else if (this.timeLineSteps['ESTIMATE'].status === 'COMPLETE' && this.timeLineSteps['PAYMENTS'].status === 'NOT_STARTED') {
            this.stepDescription = 'Your estimate is ready. Find it in the Documents section.';
        } else if (this.timeLineSteps['ESTIMATE'].status === 'COMPLETE' && this.timeLineSteps['PAYMENTS'].status === 'IN_PROGRESS') {
            this.stepDescription = 'We sent a payment for your claim. Details are in the payments section.';
        } else if (this.timeLineSteps['ESTIMATE'].status === 'COMPLETE' && this.timeLineSteps['PAYMENTS'].status === 'COMPLETE' && this.timeLineSteps['CLAIM_CLOSED'].status === 'NOT_STARTED') {
            this.stepDescription = 'We\'re reviewing your claim. Talk to your claims professional for more information.';
        } else if (this.timeLineSteps['ESTIMATE'].status === 'COMPLETE' && this.timeLineSteps['PAYMENTS'].status === 'COMPLETE' && this.timeLineSteps['CLAIM_CLOSED'].status === 'COMPLETE') {
            this.stepDescription = 'Your claim is closed. Questions? Talk to your claims professional.';
        } else if (this.timeLineSteps['ESTIMATE'].status === 'COMPLETE' && this.timeLineSteps['PAYMENTS'].status === 'NOT_APPLICABLE' && this.timeLineSteps['CLAIM_CLOSED'].status === 'COMPLETE' && this.hasClosedOutcome && this.closedOutcome === 'under_deductible') {
            this.stepDescription = 'There are no payments for this claim because your deductible covers the estimate total. Talk to your claims professional if you have questions.';
        } else if (
            this.timeLineSteps['ESTIMATE'].status === 'COMPLETE' &&
            this.timeLineSteps['PAYMENTS'].status === 'NOT_APPLICABLE' &&
            this.timeLineSteps['CLAIM_CLOSED'].status === 'COMPLETE' &&
            (!this.hasClosedOutcome || this.closedOutcome !== 'under_deductible')
        ) {
            this.stepDescription = 'There are no payments for this claim. Talk to your claims professional if you have questions.';
        }
    }
}
